import React from 'react';

import { useUserStatusContext } from '../context/UserStatus';

// The user-status effect re-runs on every token refresh and hands back a fresh `orgs` array, so
// memoizing on its identity churns every consumer's dependency chain (and re-fires their requests).
// Deriving from the joined ids keeps the result referentially stable while the grants are unchanged.
const useOrgIdsKey = (): string => {
    const { orgs } = useUserStatusContext();
    return orgs.map((org) => org.hero_organization_id).join('|');
};

/** Every org the login can reach. Stable across token refreshes. */
export const useOrgIds = (): string[] => {
    const orgIdsKey = useOrgIdsKey();
    return React.useMemo(() => (orgIdsKey ? orgIdsKey.split('|') : []), [orgIdsKey]);
};

/**
 * [HWM-23014] Org ids to send with a Form-Fills list request: every reachable org while a search
 * term is active, `undefined` otherwise so browsing stays scoped to the active provider.
 *
 * Single source of truth for the list page and the detail page's prev/next replay — if the two
 * disagree, the neighbours are computed over a different row set than the list the user searched.
 */
const useSearchOrgIds = (search?: string): string[] | undefined => {
    const orgIds = useOrgIds();
    return React.useMemo(
        () => (orgIds.length > 1 && search ? orgIds : undefined),
        [orgIds, search]
    );
};

export default useSearchOrgIds;
